import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useRouter } from '~/hooks/useRouter'
import { useWebchat } from '~/components/Webchat/useWebchat'
import { stripLocale } from '~/components/Webchat/stripLocale'
import { buildViewMessages } from '~/components/Webchat/buildViewMessages'
import { WebChat } from '~/components/Webchat/ui/WebChat'
import { ContactBar, PhoneBar } from '~/components/Webchat/ui/ContactBar'
import type { WebChatLabels } from '~/components/Webchat/ui/types'
import {
  displayNameFromUser,
  getValidUser,
  readStoredPhone,
  readStoredUser,
  startFacebookLogin,
  storePhone,
  type AuthUser,
} from '~/utils/authSession'

// Proactive-engagement timing + persistence.
// - NUDGE_DELAY_MS: cumulative time-on-site before the nudge appears.
// - NUDGE_COOLDOWN_MS: after a nudge is shown, stay quiet this long (re-shows
//   the next day, so returning visitors get nudged again).
// - VISIT_KEY (sessionStorage): per-tab visit-start timestamp. Survives
//   client-side navigation / remounts within the tab, so the delay is measured
//   against real time-on-site, not from a fresh timer on every route change.
// - SHOWN_AT_KEY (localStorage): timestamp of the last nudge, drives cooldown.
const NUDGE_DELAY_MS = 60 * 1000
const NUDGE_COOLDOWN_MS = 24 * 60 * 60 * 1000
// Floor on the arm delay after the socket connects. When cumulative time already
// exceeds NUDGE_DELAY_MS (e.g. navigating after a minute), the timer would fire
// on the next tick — before the reconnected socket reloads history, while
// messages are still []. This grace window lets history arrive so engagedRef is
// accurate at fire time, avoiding a nudge (and burned cooldown) for a returning
// visitor who already has a conversation.
const MIN_ARM_MS = 2500
const VISIT_KEY = 'webchat_visit_at'
const SHOWN_AT_KEY = 'webchat_nudged_at'

const readNum = (store: Storage | undefined, key: string): number => {
  try {
    return Number(store?.getItem(key)) || 0
  } catch {
    return 0
  }
}
const writeNum = (store: Storage | undefined, key: string, val: number) => {
  try {
    store?.setItem(key, String(val))
  } catch {
    /* ignore */
  }
}

export const FloatingMessenger: React.FC = () => {
  const router = useRouter()
  const intl = useIntl()
  const t = (id: string) => intl.formatMessage({ id })

  // Backend wants 'ru' | 'ro' lowercase + the locale-stripped page path.
  const locale = intl.locale === 'ru' ? 'ru' : 'ro'
  const path = stripLocale(router.pathname)
  const chat = useWebchat({ locale, path })
  const [contactSent, setContactSent] = useState(false)
  // Facebook identity shown above the composer ("logged in as …"). Seeded from
  // localStorage so the contact form stays hidden across reloads; covers the
  // return from the OAuth round-trip too (the callback stores the session
  // before redirecting back).
  const [fbIdentity, setFbIdentity] = useState<AuthUser | null>(() =>
    readStoredUser()
  )
  // Facebook never provides a phone number, and the backend links the chat to
  // a CRM client only when identify() carries a phone — so after the OAuth
  // round-trip we ask for it ourselves (PhoneBar) and identify only then.
  const [fbPhone, setFbPhone] = useState<string | null>(() => readStoredPhone())

  // "Continue with Facebook": an existing valid session identifies directly
  // (when the phone is already known); otherwise start the OAuth round-trip
  // (full-page navigation).
  const fbBusy = useRef(false)
  const identifiedRef = useRef(false)
  const handleFacebook = async () => {
    if (fbBusy.current) return
    fbBusy.current = true
    try {
      const user = await getValidUser()
      if (user) {
        setFbIdentity(user)
        const phone = readStoredPhone()
        if (phone) {
          identifiedRef.current = true
          chat.identify({ name: displayNameFromUser(user), contact: phone })
          setFbPhone(phone)
        }
        // No phone yet → the PhoneBar above the composer takes over.
      } else {
        startFacebookLogin(window.location.pathname + window.location.search)
      }
    } finally {
      fbBusy.current = false
    }
  }

  const handlePhoneSubmit = (phone: string) => {
    if (!fbIdentity) return
    storePhone(phone)
    setFbPhone(phone)
    identifiedRef.current = true
    chat.identify({ name: displayNameFromUser(fbIdentity), contact: phone })
  }

  // Proactive CTA: after ~1 min of cumulative time on the site, show a small
  // teaser card above the launcher (not the full panel). Replying opens the
  // full chat and drops the operator prompt in; closing just dismisses it. No
  // admin ping here — the operator only gets involved once the visitor actually
  // replies (lazy). Shown at most once per cooldown window (re-shows next day),
  // and never for a visitor who already has a conversation or opened the chat.
  const { panelOpen, injectAdminMessage } = chat
  const proactivePrompt = t('webchat.proactivePrompt')
  const [showTeaser, setShowTeaser] = useState(false)

  // Read the latest engagement state at fire time without restarting the timer
  // on every render (history arrives async, after the socket connects).
  const engagedRef = useRef(false)
  useEffect(() => {
    engagedRef.current = panelOpen || chat.messages.length > 0
  })

  const { connection } = chat

  // A Facebook-identified visitor re-identifies once per page load so the
  // operator always has the contact, even for a fresh chat session on a new
  // device/tab. Sent from the stored user (no /me round-trip) — identify only
  // carries display data, nothing security-sensitive. Waits for the phone:
  // identify with a phone contact is what links the chat to a CRM client.
  useEffect(() => {
    if (connection !== 'connected') return
    if (!fbIdentity || !fbPhone || identifiedRef.current) return
    identifiedRef.current = true
    chat.identify({
      name: displayNameFromUser(fbIdentity),
      contact: fbPhone,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, fbIdentity, fbPhone])

  useEffect(() => {
    // Wait for the socket: scheduling while 'connecting' can fire before history
    // loads, and the panel isn't rendered until connected anyway.
    if (connection !== 'connected') return
    if (engagedRef.current || showTeaser) return
    const session =
      typeof window !== 'undefined' ? window.sessionStorage : undefined
    const local = typeof window !== 'undefined' ? window.localStorage : undefined

    const now = Date.now()
    // Still cooling down from the last nudge → stay quiet.
    if (now - readNum(local, SHOWN_AT_KEY) < NUDGE_COOLDOWN_MS) return

    // Anchor the delay to when this tab-visit began, so navigating between
    // pages (which remounts this component) doesn't reset the countdown. Floor
    // the wait at MIN_ARM_MS so a stale anchor can't fire before history loads.
    let visitAt = readNum(session, VISIT_KEY)
    if (!visitAt) {
      visitAt = now
      writeNum(session, VISIT_KEY, visitAt)
    }
    const remaining = Math.max(MIN_ARM_MS, NUDGE_DELAY_MS - (now - visitAt))

    const id = window.setTimeout(() => {
      // Re-check at fire time: history may now show the visitor is engaged, and
      // another tab may have shown the nudge (and written the cooldown) since we
      // armed this timer.
      if (engagedRef.current) return
      if (Date.now() - readNum(local, SHOWN_AT_KEY) < NUDGE_COOLDOWN_MS) return
      writeNum(local, SHOWN_AT_KEY, Date.now())
      setShowTeaser(true)
    }, remaining)
    return () => window.clearTimeout(id)
  }, [connection, panelOpen, showTeaser])

  const handleTeaserReply = () => {
    setShowTeaser(false)
    injectAdminMessage(proactivePrompt) // shows the prompt + auto-opens the panel
  }

  // Only surface the chat once the socket is actually connected.
  if (chat.connection !== 'connected') return null

  const isConfiguratorPage = router.asPath.includes('/configurator')

  const dayKey = (iso: string) => {
    const d = new Date(iso)
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  }
  const todayKey = dayKey(new Date().toISOString())
  const messages = buildViewMessages(chat.messages, {
    time: (iso) =>
      new Date(iso).toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      }),
    dayKey,
    dayLabel: (iso) =>
      dayKey(iso) === todayKey
        ? t('webchat.today')
        : new Date(iso).toLocaleDateString(locale, {
            day: 'numeric',
            month: 'long',
          }),
  })

  const labels: WebChatLabels = {
    launcherOpen: t('webchat.launcherOpen'),
    launcherClose: t('webchat.launcherClose'),
    close: t('webchat.close'),
    placeholder: t('webchat.placeholder'),
    send: t('webchat.send'),
    statusOnline: t('webchat.status.online'),
    statusAway: t('webchat.status.online'),
    statusOffline: t('webchat.status.online'),
    receiptSending: t('webchat.receipt.sending'),
    receiptSent: t('webchat.receipt.sent'),
    receiptRead: t('webchat.receipt.read'),
    receiptFailed: t('webchat.failed'),
    senderOperator: t('webchat.sender.operator'),
    senderYou: t('webchat.sender.you'),
  }

  const role = t('webchat.role')
  const closed = chat.sessionState === 'CLOSED'
  const notice = chat.terminalError
    ? t('webchat.error')
    : closed
      ? t('webchat.closed')
      : undefined
  const showContact = !notice && !contactSent && !fbIdentity

  return (
    <WebChat
      rootClassName={isConfiguratorPage ? 'wc-root--configurator' : undefined}
      operator={{ name: 'Elizabet', role }}
      status="online"
      statusLabel={`${labels.statusOnline} · ${role}`}
      messages={messages}
      labels={labels}
      typingLabel={t('webchat.typing')}
      greeting={t('webchat.greeting')}
      unreadCount={chat.unreadCount}
      notice={notice}
      composerDisabled={!!notice}
      open={chat.panelOpen}
      teaser={
        showTeaser && !notice && chat.messages.length === 0
          ? {
              text: proactivePrompt,
              replyLabel: t('webchat.ctaReply'),
              closeLabel: t('webchat.close'),
              onReply: handleTeaserReply,
              onClose: () => setShowTeaser(false),
            }
          : undefined
      }
      onOpenChange={(o) => {
        // Manually opening the chat retires the teaser this session, so it
        // can't pop back when the panel is closed again.
        if (o) {
          setShowTeaser(false)
          chat.openPanel()
        } else {
          chat.closePanel()
        }
      }}
      onSend={chat.send}
      aboveComposer={
        fbIdentity && !fbPhone && !notice ? (
          <PhoneBar
            prompt={t('webchat.phonePrompt')}
            phonePlaceholder={t('webchat.contactPhone')}
            saveLabel={t('webchat.contactSave')}
            onSubmit={handlePhoneSubmit}
          />
        ) : fbIdentity && !notice ? (
          <div className="wc-contact wc-identified">
            <span className="wc-contact-prompt">
              {intl.formatMessage(
                { id: 'webchat.identifiedAs' },
                { name: displayNameFromUser(fbIdentity) }
              )}
            </span>
          </div>
        ) : showContact ? (
          <ContactBar
            prompt={t('webchat.contactPrompt')}
            namePlaceholder={t('webchat.contactName')}
            phonePlaceholder={t('webchat.contactPhone')}
            saveLabel={t('webchat.contactSave')}
            facebookLabel={t('webchat.contactFacebook')}
            onSubmit={(payload) => {
              chat.identify(payload)
              setContactSent(true)
            }}
            onFacebook={handleFacebook}
          />
        ) : undefined
      }
    />
  )
}
